import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { TrendingUp, TrendingDown, Minus, Home, Calendar, BedDouble, XCircle, Euro, Percent, DollarSign } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/config';

const API = `${BACKEND_URL}/api`;

const COLORS = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6'];

const KPIOccupazione = ({ filters }) => {
  const [occupancyData, setOccupancyData] = useState(null);
  const [previousYearData, setPreviousYearData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOccupancyData();
  }, [filters]);

  const fetchOccupancyData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      
      // Build query params
      const params = new URLSearchParams({
        year: filters.year
      });
      
      if (filters.period === 'month' && filters.month) {
        params.append('month', filters.month);
      }
      if (filters.period === 'quarter' && filters.quarter) {
        params.append('quarter', filters.quarter);
      }
      if (filters.propertyId) {
        params.append('property_id', filters.propertyId);
      }
      if (filters.country) {
        params.append('country', filters.country);
      }
      if (filters.region) {
        params.append('region', filters.region);
      }
      if (filters.province) {
        params.append('province', filters.province);
      }
      if (filters.city) {
        params.append('city', filters.city);
      }
      
      const response = await axios.get(`${API}/kpi/occupazione?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOccupancyData(response.data);
      
      // Fetch previous year for comparison
      const prevParams = new URLSearchParams({
        year: filters.year - 1
      });
      if (filters.period === 'month' && filters.month) {
        prevParams.append('month', filters.month);
      }
      if (filters.period === 'quarter' && filters.quarter) {
        prevParams.append('quarter', filters.quarter);
      }
      if (filters.propertyId) {
        prevParams.append('property_id', filters.propertyId);
      }
      if (filters.country) {
        prevParams.append('country', filters.country);
      }
      if (filters.region) {
        prevParams.append('region', filters.region);
      }
      if (filters.province) {
        prevParams.append('province', filters.province);
      }
      if (filters.city) {
        prevParams.append('city', filters.city);
      }
      
      const prevResponse = await axios.get(`${API}/kpi/occupazione?${prevParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviousYearData(prevResponse.data);
      
    } catch (error) {
      console.error('Error fetching occupancy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  if (loading || !occupancyData) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  const changes = previousYearData ? {
    occupancyRate: calculateChange(occupancyData.occupancy_rate, previousYearData.occupancy_rate),
    bookedNights: calculateChange(occupancyData.total_booked_nights, previousYearData.total_booked_nights),
    revenue: calculateChange(occupancyData.total_revenue, previousYearData.total_revenue),
    adr: calculateChange(occupancyData.adr, previousYearData.adr),
    revpar: calculateChange(occupancyData.revpar, previousYearData.revpar),
    lostRevenue: calculateChange(occupancyData.lost_revenue, previousYearData.lost_revenue)
  } : null;

  // Prepare pie chart data
  const distributionData = [
    { name: 'Notti Vendute', value: occupancyData.total_booked_nights, color: '#10b981' },
    { name: 'Notti Invendute', value: occupancyData.total_unsold_nights, color: '#ef4444' },
    { name: 'Notti Chiuse', value: occupancyData.total_closed_nights, color: '#f59e0b' }
  ];

  const StatCard = ({ title, currentValue, change, icon: Icon, color, subtitle, trend }) => {
    const TrendIcon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
    const trendColor = trend === 'positive' 
      ? (change > 0 ? 'text-green-600' : 'text-red-600')
      : trend === 'negative'
      ? (change > 0 ? 'text-red-600' : 'text-green-600')
      : (change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600');
    
    return (
      <Card className="p-6 bg-gradient-to-br from-white to-gray-50 shadow-lg hover:shadow-xl transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
            <p className={`text-3xl font-bold ${color}`}>{currentValue}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
            
            {change !== null && change !== undefined && (
              <div className="mt-3 flex items-center space-x-2">
                <TrendIcon className={`h-4 w-4 ${trendColor}`} />
                <span className={`text-sm font-semibold ${trendColor}`}>
                  {change > 0 ? '+' : ''}{change.toFixed(1)}%
                </span>
                <span className="text-xs text-gray-500">vs {filters.year - 1}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full bg-gradient-to-br ${color.replace('text-', 'from-')}-100 ${color.replace('text-', 'to-')}-200`}>
            <Icon className={`h-8 w-8 ${color}`} />
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">🏨 KPI Occupazione</h2>
        <p className="text-gray-600">Analisi dettagliata dell'occupazione e disponibilità</p>
      </div>

      {/* Main Occupancy KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Tasso di Occupazione"
          currentValue={`${occupancyData.occupancy_rate.toFixed(1)}%`}
          change={changes?.occupancyRate}
          icon={Percent}
          color="text-blue-600"
          subtitle="Notti vendute / Notti prenotabili"
          trend="positive"
        />
        
        <StatCard
          title="Notti Vendute"
          currentValue={occupancyData.total_booked_nights.toLocaleString()}
          change={changes?.bookedNights}
          icon={BedDouble}
          color="text-green-600"
          subtitle={`Su ${occupancyData.total_bookable_nights.toLocaleString()} prenotabili`}
          trend="positive"
        />
        
        <StatCard
          title="Notti Invendute"
          currentValue={occupancyData.total_unsold_nights.toLocaleString()}
          change={changes ? calculateChange(occupancyData.total_unsold_nights, previousYearData.total_unsold_nights) : null}
          icon={XCircle}
          color="text-red-600"
          subtitle="Opportunità perse"
          trend="negative"
        />
      </div>

      {/* Nights Breakdown */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">📊 Composizione Notti</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard
            title="Notti Disponibili Totali"
            currentValue={occupancyData.total_available_nights.toLocaleString()}
            change={null}
            icon={Calendar}
            color="text-slate-600"
            subtitle={`${occupancyData.properties_count} immobili x ${occupancyData.period_days} giorni`}
          />
          
          <StatCard
            title="Notti Chiuse"
            currentValue={occupancyData.total_closed_nights.toLocaleString()}
            change={changes ? calculateChange(occupancyData.total_closed_nights, previousYearData.total_closed_nights) : null}
            icon={XCircle}
            color="text-orange-600"
            subtitle="Per manutenzione o altro"
          />
        </div>
      </div>

      {/* Financial KPIs */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">💰 KPI Finanziari</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Ricavi Totali"
            currentValue={`€${occupancyData.total_revenue.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
            change={changes?.revenue}
            icon={Euro}
            color="text-emerald-600"
            trend="positive"
          />
          
          <StatCard
            title="ADR"
            currentValue={`€${occupancyData.adr.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
            change={changes?.adr}
            icon={DollarSign}
            color="text-blue-600"
            subtitle="Average Daily Rate"
            trend="positive"
          />
          
          <StatCard
            title="RevPAR"
            currentValue={`€${occupancyData.revpar.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
            change={changes?.revpar}
            icon={TrendingUp}
            color="text-cyan-600"
            subtitle="Revenue Per Available Room"
            trend="positive"
          />
          
          <StatCard
            title="Fatturato Perso"
            currentValue={`€${occupancyData.lost_revenue.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
            change={changes?.lostRevenue}
            icon={TrendingDown}
            color="text-red-600"
            subtitle="Notti invendute x ADR"
            trend="negative"
          />
        </div>
      </div>

      {/* Distribution Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📊 Distribuzione Notti</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={distributionData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, value }) => `${name}: ${value.toLocaleString()}`}
                outerRadius={100}
                dataKey="value"
              >
                {distributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => value.toLocaleString()}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Bar Chart */}
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={distributionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip 
                formatter={(value) => value.toLocaleString()}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {distributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Summary Table */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📋 Riepilogo Completo</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Metrica</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">{filters.year}</th>
                {previousYearData && (
                  <>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">{filters.year - 1}</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Variazione</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4">Immobili Attivi</td>
                <td className="text-right py-3 px-4 font-semibold">{occupancyData.properties_count}</td>
                {previousYearData && (
                  <>
                    <td className="text-right py-3 px-4 text-gray-600">{previousYearData.properties_count}</td>
                    <td className="text-right py-3 px-4">
                      <span className={`font-semibold ${occupancyData.properties_count >= previousYearData.properties_count ? 'text-green-600' : 'text-red-600'}`}>
                        {occupancyData.properties_count - previousYearData.properties_count}
                      </span>
                    </td>
                  </>
                )}
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4">Notti Disponibili</td>
                <td className="text-right py-3 px-4 font-semibold">{occupancyData.total_available_nights.toLocaleString()}</td>
                {previousYearData && (
                  <>
                    <td className="text-right py-3 px-4 text-gray-600">{previousYearData.total_available_nights.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">-</td>
                  </>
                )}
              </tr>
              <tr className="border-b border-gray-100 bg-orange-50">
                <td className="py-3 px-4 font-medium">Notti Chiuse</td>
                <td className="text-right py-3 px-4 font-semibold text-orange-600">{occupancyData.total_closed_nights.toLocaleString()}</td>
                {previousYearData && (
                  <>
                    <td className="text-right py-3 px-4 text-gray-600">{previousYearData.total_closed_nights.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">-</td>
                  </>
                )}
              </tr>
              <tr className="border-b border-gray-100 bg-blue-50">
                <td className="py-3 px-4 font-medium">Notti Prenotabili</td>
                <td className="text-right py-3 px-4 font-semibold text-blue-600">{occupancyData.total_bookable_nights.toLocaleString()}</td>
                {previousYearData && (
                  <>
                    <td className="text-right py-3 px-4 text-gray-600">{previousYearData.total_bookable_nights.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">-</td>
                  </>
                )}
              </tr>
              <tr className="border-b border-gray-100 bg-green-50">
                <td className="py-3 px-4 font-medium">Notti Vendute</td>
                <td className="text-right py-3 px-4 font-semibold text-green-600">{occupancyData.total_booked_nights.toLocaleString()}</td>
                {previousYearData && (
                  <>
                    <td className="text-right py-3 px-4 text-gray-600">{previousYearData.total_booked_nights.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">
                      <span className={`font-semibold ${changes.bookedNights > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {changes.bookedNights > 0 ? '+' : ''}{changes.bookedNights.toFixed(1)}%
                      </span>
                    </td>
                  </>
                )}
              </tr>
              <tr className="border-b border-gray-100 bg-red-50">
                <td className="py-3 px-4 font-medium">Notti Invendute</td>
                <td className="text-right py-3 px-4 font-semibold text-red-600">{occupancyData.total_unsold_nights.toLocaleString()}</td>
                {previousYearData && (
                  <>
                    <td className="text-right py-3 px-4 text-gray-600">{previousYearData.total_unsold_nights.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">-</td>
                  </>
                )}
              </tr>
              <tr className="border-b-2 border-gray-200 bg-blue-100">
                <td className="py-3 px-4 font-bold">Tasso di Occupazione</td>
                <td className="text-right py-3 px-4 font-bold text-blue-900">{occupancyData.occupancy_rate.toFixed(1)}%</td>
                {previousYearData && (
                  <>
                    <td className="text-right py-3 px-4 font-semibold text-gray-700">{previousYearData.occupancy_rate.toFixed(1)}%</td>
                    <td className="text-right py-3 px-4">
                      <span className={`font-bold ${changes.occupancyRate > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {changes.occupancyRate > 0 ? '+' : ''}{changes.occupancyRate.toFixed(1)}%
                      </span>
                    </td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default KPIOccupazione;
